// @flow
/** @jsx jsx */

import { jsx } from '@emotion/core';
import * as React from 'react';
import cuid from 'cuid';
import { Formik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { css } from '@emotion/core';
import Glyphicon from 'react-bootstrap/lib/Glyphicon';
import { Mutation } from 'react-apollo';
import Label from 'react-bootstrap/lib/Label';

import DropdownField from '../DropdownField';
import DatePickerField from '../DatePickerField';
import type { CommonActionProps } from './types.actions';
import ButtonAction from './ButtonAction';
import StatusController from '../../controllers/StatusController';
import { editOrderMutation } from '../../graphql/mutations/orders/editOrder.mutation';
import getMenuItems from '../../graphql/queries/getMenuItems.queries';
import createOrderContentQuery from '../../graphql/queries/createOrderContent.query';
import getActiveQueryQuery from '../../graphql/queries/getActiveOrder.query';
import messages from '../../config/messages';
import type { ActionStatus } from '../../types';
import {
  removeTypenamesFromOrderDet,
  getDateFormat,
  removeTypeNamesFromOrderAccount,
} from '../../utils';
import { DesktopView, MobileView } from '../../config/mediaQuery';
import { Link } from 'react-router-dom';

const EditOrderSchema = Yup.object().shape({
  OrderType: Yup.string().required('Type is required'),
  OrderDate: Yup.string().required('Date is required'),
});

type Props = {
  OrderAccount: any,
  OrderDate: string,
  OrderType: string,
  holidays: Array<string>,
  disabledDays: Array<number>,
  minSelectableDay: string,
  maxSelectableDay: string,
  typeOptions: Array<{
    id: string,
    value: string | number | boolean,
    text: string,
  }>,
  buttonText?: string,
  buttonTextLoading?: string,
  buttonTextCompleted?: string,
  isValidType: boolean,
  isValidDate: boolean,
  notifyFormValidity: (opt: {
    dirty: boolean,
    saving?: boolean,
    transmitting?: boolean,
    invalidDate?: boolean,
    invalidType?: boolean,
    OrderDate?: string,
  }) => mixed,
  onChangeOrderType: any,
  onChangeOrderDate: any,
  shouldHaveDatePicker?: boolean,
  Det: any,
  refetch: () => mixed,
  dirty?: boolean,
  onOrderSaved: string => mixed,
  onComplete?: () => mixed,
};

type State = {
  dateValue: moment | null,
  errorMessage: string,
  cleared: boolean,
  validDate: boolean,
  hasSelectedDate: boolean,
};

const getBorderColor = (status: ActionStatus, isValid: boolean) => {
  if (!isValid && status !== 'loading') {
    return 'border-color: red;';
  }

  return '';
};

class Notifyier extends React.Component<{
  invalidDate: boolean,
  invalidType: boolean,
  setFieldError: any,
  notifyFormValidity: (opt: {
    dirty: boolean,
    saving?: boolean,
    transmitting?: boolean,
    invalidDate?: boolean,
    invalidType?: boolean,
    OrderDate?: string,
  }) => mixed,
  dirty: boolean,
  errors: any,
  OrderType?: string,
  OrderDate?: string,
}> {
  componentDidUpdate(prevProps) {
    if (
      (prevProps.invalidType &&
        prevProps.errors.OrderType !== this.props.errors.OrderType) ||
      (prevProps.invalidDate &&
        prevProps.errors.OrderDate !== this.props.errors.OrderDate) ||
      prevProps.dirty !== this.props.dirty
    ) {
      const { setFieldError } = prevProps;

      if (prevProps.invalidDate) {
        setFieldError('OrderDate', 'Invalid Date');
      }

      this.props.notifyFormValidity({
        dirty: this.props.dirty,
        invalidDate: this.props.invalidDate,
        invalidType: !!this.props.errors.OrderType,
        OrderDate: this.props.OrderDate,
      });
    }
  }

  render() {
    return null;
  }
}

export default class EditOrderAction extends React.Component<
  Props & CommonActionProps,
  State,
> {
  static defaultProps = {
    buttonText: 'Save order',
    buttonTextLoading: 'Saving order...',
    buttonTextCompleted: 'Order saved!',
  };

  constructor(props: Props & CommonActionProps) {
    super(props);

    const dateValue = props.OrderDate
      ? moment(props.OrderDate, 'YYMMDD')
      : null;

    this.state = {
      dateValue,
      errorMessage: '',
      cleared: false,
      validDate: props.isValidDate,
      hasSelectedDate: false,
    };
  }

  oldDerivedStateFromProps(
    nextProps: Props & CommonActionProps,
    state: State,
  ) {
    // This can happen when an order is deleted
    if (nextProps.OrderDate === null) {
      return this.setState({
        dateValue: null,
        errorMessage: '',
        cleared: false,
        hasSelectedDate: false,
      });
    }

    if (
      nextProps.OrderDate &&
      state.cleared === false && // cleared !== false only when the user manually cleared the date.
      state.dateValue === null
    ) {
      return this.setState({
        dateValue: moment(nextProps.OrderDate, 'YYMMDD'),
      });
    }

    if (
      nextProps.shouldHaveDatePicker &&
      nextProps.isValidDate !== undefined &&
      nextProps.isValidDate !== state.validDate &&
      state.cleared === false // cleared !== false only when the user manually cleared the date.
    ) {
      return this.setState({
        validDate: nextProps.isValidDate,
      });
    }

    return null;
  }

  componentDidMount() {
    this.oldDerivedStateFromProps(this.props, this.state);
  }

  componentDidUpdate(prevProps: Props & CommonActionProps) {
    if (this.props !== prevProps) {
      this.oldDerivedStateFromProps(this.props, this.state);
    }
  }

  getMonthFromDate = date =>
    date && moment(date, getDateFormat('server')).month();

  onDateChange = ({ date }: { date: moment }) => {
    const { onChangeOrderDate } = this.props;

    this.setState({
      dateValue: moment(date),
      cleared: date === null,
      validDate: date !== null,
      hasSelectedDate: date !== null,
    });

    if (date && typeof onChangeOrderDate === 'function') {
      const typeFormat = getDateFormat('server');
      // $FlowFixMe
      onChangeOrderDate((date.format(typeFormat): ''));
    }
  };

  onMutationCompleted = ({ resetForm, data, values }: any) => {
    if (data.editOrder && data.editOrder.error) {
      return;
    }

    this.props.notifyFormValidity({
      dirty: false,
      saving: false,
    });

    // this will reset things like the selected Order Type or
    // if the Order has unsaved changes.
    this.props.onOrderSaved('');

    resetForm();
  };

  onSubmit = async ({ mutation, values }: any) => {
    this.props.notifyFormValidity({
      dirty: true,
      saving: true,
    });

    try {
      const { data } = await mutation();

      const { editOrder } = data;

      if (editOrder && editOrder.error) {
        const { errorMessage } = editOrder.error.data;

        this.setState({
          errorMessage,
        });
      } else if (this.state.errorMessage) {
        this.setState({
          errorMessage: '',
        });
      }

      const { originalValues } = editOrder;

      const originalMonth = this.getMonthFromDate(originalValues.OrderDate);
      const newMonth = this.getMonthFromDate(values.OrderDate);

      const hasMonthChanged = originalMonth !== newMonth;

      const hasReasonToRefetch =
        (this.props.Det &&
          this.props.Det.some(
            i =>
              i.maxItemQty &&
              i.maxItemQty.ActionPerOrderType !== undefined &&
              i.maxItemQty.ActionPerOrderType !== 'none',
          )) ||
        hasMonthChanged;

      if (typeof this.props.refetch === 'function' && hasReasonToRefetch) {
        this.props.refetch();
      }
    } catch (e) {
      const cleanedMessage = e.message
        ? e.message.replace('GraphQL error: ', '')
        : '';

      this.setState({
        errorMessage: cleanedMessage,
      });
    }
  };


  render() {
    const { displayMenu, editOrderNotesAction, mobo, itemHistory, removeOrder, exportAction, transmitOrderAction } = this.props.formItems;

    return (
      <Formik
        initialValues={{
          OrderType: this.props.OrderType,
          OrderDate: this.props.OrderDate,
        }}
        validationSchema={EditOrderSchema}
        enableReinitialize
        isInitialValid={!this.props.isValidDate && !this.props.isValidType}
      >
        {({
          values,
          errors,
          touched,
          isValid,
          setFieldValue,
          setFieldTouched,
          setFieldError,
          resetForm,
          dirty,
        }) => {
          return (
            <Mutation
              mutation={editOrderMutation}
              variables={{
                input: {
                  ...values,
                  Det: removeTypenamesFromOrderDet(this.props.Det),
                },
              }}
              onCompleted={onCompleteData =>
                this.onMutationCompleted({
                  data: onCompleteData,
                  isValid,
                  resetForm,
                  values,
                })
              }
              refetchQueries={() => {
                return [
                  {
                    query: getMenuItems,
                  },
                  {
                    query: createOrderContentQuery,
                    variables: {
                      selectedRow: removeTypeNamesFromOrderAccount(
                        this.props.OrderAccount,
                      ),
                    },
                  },
                  {
                    query: getActiveQueryQuery,
                  },
                ];
              }}
              awaitRefetchQueries
            >
              {(mutation, { loading, error, data: dataMutation }: any) => (
                <StatusController
                  hasError={
                    !!error ||
                    (dataMutation &&
                      dataMutation.editOrder &&
                      dataMutation.editOrder.error)
                  }
                  loading={loading}
                >
                  {({ status }) => {
                    const dropdown = <DropdownField
                      value={values.OrderType}
                      options={
                        this.props.typeOptions || [
                          {
                            id: '',
                            text: '...',
                            value: '',
                          },
                        ]
                      }
                      id={`type-${cuid()}`}
                      title="Type"
                      name="OrderType"
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      errors={errors}
                      touched={touched}
                      className={css`
                      margin-bottom: 0;
                      padding: 0 0 4px 0;
                      // zIndex: 10000;
                    `}
                      labelClassName={css`
                      flex-basis: 50px;
                    `}
                      disabled={this.props.disabled || loading}
                      showErrorMsg={false}
                      onChange={this.props.onChangeOrderType}
                    />
                    const datePicker = <DatePickerField
                      label="Date"
                      name="OrderDate"
                      onChange={d => {
                        this.onDateChange({
                          date: d,
                          dirty,
                        });
                      }}
                      value={this.state.dateValue}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      errors={errors}
                      touched={touched}
                      holidays={this.props.holidays}
                      disabledDays={this.props.disabledDays}
                      minSelectableDay={this.props.minSelectableDay}
                      maxSelectableDay={this.props.maxSelectableDay}
                      className={css`
                      margin-bottom: 0;
                      padding: 2px 0;
                    `}
                      inputClassName={css`
                      height: 30px;
                      ${getBorderColor(
                        status,
                        this.state.hasSelectedDate
                          ? this.state.validDate
                          : this.props.isValidDate,
                      )};
                    `}
                      labelClassName={css`
                      flex-basis: 50px;
                    `}
                      inputContainerClassName={css`
                      max-width: 130px;
                    `}
                      disabled={this.props.disabled || loading}
                      showErrorMsg={false}
                      visible={this.props.shouldHaveDatePicker}
                      isDateValid={this.state.hasSelectedDate
                        ? this.state.validDate
                        : this.props.isValidDate}
                    />

                    const mobileSave = <button
                      onClick={() =>
                        this.onSubmit({
                          mutation,
                          values,
                        })
                      }
                      disabled={
                        this.props.disabled ||
                        /**
                         * dirty is from formik, this.props.dirty comes from changes
                         * made outside this component but affects the state of this form
                         */
                        // Note: Temporarely disabling dirty so the button
                        // is always active when needed.
                        // (!dirty && !this.props.dirty) ||
                        (this.props.shouldHaveDatePicker
                          ? Object.keys(errors).length > 0
                          : errors.OrderType) ||
                        loading ||
                        (this.props.shouldHaveDatePicker &&
                          !this.props.isValidDate) ||
                        !this.props.isValidType
                      }>
                      <img src='assets/saved-black.svg' alt='save icon'/>
                      {(status === '' || status === 'error') &&
                        this.props.buttonText}
                      {status === 'loading' &&
                        this.props.buttonTextLoading}
                      {status === 'completed' &&
                        this.props.buttonTextCompleted}
                    </button>

                    return (
                      <>
                        <DesktopView>
                          <div
                            css={{
                              display: 'flex',
                              flexDirection: 'row',
                              position: 'relative',
                            }}
                          >
                            {(this.state.errorMessage ||
                              (error && Object.keys(error).length)) && (
                                <div
                                  css={{
                                    position: 'absolute',
                                    top: '-25px',
                                    left: '7px',
                                  }}
                                >
                                  <Label bsStyle="danger">
                                    {messages.editOrder.fail}
                                  </Label>
                                </div>
                              )}
                            <div
                              css={{
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <Notifyier
                                invalidType={!this.props.isValidType}
                                invalidDate={!this.state.validDate}
                                notifyFormValidity={this.props.notifyFormValidity}
                                setFieldError={setFieldError}
                                dirty={dirty}
                                errors={errors}
                                {...values}
                              />

                              {dropdown}
                              {datePicker}
                            </div>
                            <div
                              css={{
                                paddingLeft: '10px',
                              }}
                            >
                              <DesktopView>
                                <ButtonAction
                                  bsSize="xsmall"
                                  onClick={() =>
                                    this.onSubmit({
                                      mutation,
                                      values,
                                    })
                                  }
                                  disabled={
                                    this.props.disabled ||
                                    /**
                                     * dirty is from formik, this.props.dirty comes from changes
                                     * made outside this component but affects the state of this form
                                     */
                                    // Note: Temporarely disabling dirty so the button
                                    // is always active when needed.
                                    // (!dirty && !this.props.dirty) ||
                                    (this.props.shouldHaveDatePicker
                                      ? Object.keys(errors).length > 0
                                      : errors.OrderType) ||
                                    loading ||
                                    (this.props.shouldHaveDatePicker &&
                                      !this.props.isValidDate) ||
                                    !this.props.isValidType
                                  }
                                >
                                  <Glyphicon glyph="floppy-disk" />{' '}
                                  <span>
                                    {(status === '' || status === 'error') &&
                                      this.props.buttonText}
                                    {status === 'loading' &&
                                      this.props.buttonTextLoading}
                                    {status === 'completed' &&
                                      this.props.buttonTextCompleted}
                                  </span>
                                </ButtonAction>
                              </DesktopView>
                            </div>
                          </div>
                        </DesktopView>
                        <MobileView>
                          <div className='action-menu-items'>
                            <Notifyier
                              invalidType={!this.props.isValidType}
                              invalidDate={!this.state.validDate}
                              notifyFormValidity={this.props.notifyFormValidity}
                              setFieldError={setFieldError}
                              dirty={dirty}
                              errors={errors}
                              {...values}
                            />
                            <table>
                              {displayMenu &&
                                <tbody>
                                  <tr>
                                    <td style={{ paddingLeft: 10 }}>{dropdown}</td>
                                    <td style={{ paddingLeft: 10 }}>{datePicker}</td>
                                  </tr>
                                  <tr>
                                    <td>{editOrderNotesAction}</td>
                                    <td>{mobo}</td>
                                  </tr>
                                  <tr>
                                    <td>{itemHistory}</td>
                                    <td>{mobileSave}</td>
                                  </tr>
                                  <tr>
                                    <td>{removeOrder}</td>
                                    <td>{exportAction}</td>
                                  </tr>
                                  <tr>
                                    <td>{transmitOrderAction}</td>
                                    <td>
                                      <Link to='/inventory'>
                                        <button>
                                          <img src='assets/inventory.svg' alt='inventory icon' />
                                          Inventory
                                        </button>
                                      </Link>
                                    </td>
                                  </tr>
                                </tbody>
                              }
                            </table>
                          </div>
                        </MobileView>
                      </>
                    );
                  }}
                </StatusController>
              )}
            </Mutation>
          );
        }}
      </Formik>
    );
  }
}
